/**
 * The cap field: what a person types, what it is refused for, and what saving it
 * states.
 *
 * `mockups/client/settings.html` §Widget caps draws a text box holding `3,000` and,
 * beside it, `Worst case at this cap · $240.00`. Three things follow from that and
 * from `PATCH /api/clients/me/widgets/:ref/cap`:
 *
 *   1. **The box holds text, not a number.** The mockup prints the thousands
 *      separator, so the field is a string the page formats into and parses out of;
 *      `capOf` is the one parser and `CapFieldSchema` is what refuses the rest.
 *   2. **A cap below the allowance is refused here, in the field.** `usageBillable`
 *      throws a `DomainError` on one and `usage.service.setCap` refuses it, so
 *      without this the reader typed `500`, the worst case became `null`, the
 *      confirm dialog rendered nothing and the page said "Saved." for a write that
 *      was never sent. `capsFormSchema` is what makes it a field error instead.
 *   3. **Saving has to state the worst case.** The route's body is
 *      `{ monthly_cap, worst_case }` and the service refuses a statement that does
 *      not match — the same rule every `$` action obeys, because `amountOf` is pure
 *      over a tool's input (`actions/define.ts`) and an amount that lived only on
 *      the row would reach no money limit at all.
 *
 * ## `worstCaseAt` is the one arithmetic on this page, and here is the move that
 * ends it
 *
 * The single source is `apps/api/src/modules/usage/service.ts` `worstCase(row)`,
 * which is what `WidgetView.worst_case` is computed by. `.docs/19-client-portal-pages.md`
 * §Design decisions — "Pages compute nothing. Every figure comes formatted-ready
 * from a read model" — is obeyed for the **stored** cap: `WidgetCapsPanel` renders
 * `widget.worst_case` and this function is not called at all. It cannot be obeyed
 * for a cap the reader has just typed: no route prices one that has not been saved,
 * and the write will not accept a cap without its worst case. So this calls the same
 * two `packages/shared` functions the service calls — `usageBillable` then
 * `multiplyByQty` — rather than multiplying anything itself.
 *
 * The migration is two steps and no behaviour change:
 *
 *   1. move the body into `packages/shared/src/pricing.ts` as
 *      `worstCaseAtCap(unitAmount, includedQty, cap)` — it already reads nothing but
 *      those three values and two functions that live in that file;
 *   2. have `usage/service.ts` `worstCase(row)` and this page both call it. The
 *      service keeps its `WidgetRow` signature; this page keeps its `WidgetView` one.
 *
 * `SettingsPage.test.tsx` pins this function's answer for the saved cap to the
 * `worst_case` the API recorded, so the two are provably the same arithmetic until
 * they are the same function.
 */

import { isMetered, worstCaseAtCap, type Money, type WidgetView } from '@fareast/shared'
import { z } from 'zod'

/**
 * A whole number of units, thousands separators allowed.
 *
 * The route's own schema is `z.int().min(0).max(10_000_000)`
 * (`apps/api/src/modules/usage/routes.ts` `CapSchema`) and lives there rather than
 * in `packages/shared`, so there is no shared object to resolve with — the ceiling
 * below is that route's, restated. Reported to the lead.
 */
export const CAP_MAX = 10_000_000

export const CapFieldSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}(,\d{3})*$|^\d+$/, 'A cap is a whole number of units, like 3,000.')
  .refine((text) => capOf(text) <= CAP_MAX, 'That cap is larger than we will accept.')

/** One row of the caps form: which widget, and what the box holds. */
export const CapsFormSchema = z.strictObject({
  caps: z.array(z.strictObject({ ref: z.string(), monthlyCap: CapFieldSchema })),
})

export type CapsFormInput = z.input<typeof CapsFormSchema>

/**
 * The form, with each row held to its own widget's allowance.
 *
 * A factory rather than a constant because the floor is per widget and the refusal
 * has to name it — and it has to name it **formatted**, `1,000` rather than `1000`,
 * which is `useFormat()`'s job and no schema's. Both arguments are what the page
 * already has: `SettingsPage` builds this beside `capsValuesOf`, from the same two
 * things.
 *
 * The refinement is skipped for a row the field schema already refused, so a
 * half-typed number says one thing rather than two.
 */
export function capsFormSchema(
  widgets: readonly WidgetView[],
  formatNumber: (value: number) => string,
) {
  const allowances = new Map(widgets.map((widget) => [widget.ref, widget.included_qty]))

  return CapsFormSchema.superRefine((values, ctx) => {
    values.caps.forEach((row, index) => {
      const included = allowances.get(row.ref)
      if (included === undefined || capOf(row.monthlyCap) >= included) return
      ctx.addIssue({
        code: 'custom',
        path: ['caps', index, 'monthlyCap'],
        message:
          `A cap has to be at least the ${formatNumber(included)} your package ` +
          'includes — below that we would refuse work you have already paid for.',
      })
    })
  })
}

/**
 * The widgets that have a cap to set — every widget charged per unit, and no other.
 *
 * A widget with no unit price has no allowance and no cap (session 46c,
 * `pricing.isMetered`): the API refuses a cap on one, so it gets no box, no worst case
 * and no row in the form. One filter, read by the form's schema, its starting values,
 * the save and the panel alike, so the four cannot disagree about which widgets are in
 * the form.
 */
export function cappedWidgets(widgets: readonly WidgetView[]): WidgetView[] {
  return widgets.filter((widget) => isMetered(widget.unit_amount.amount))
}

/** `"3,000"` → `3000`. The separator is display; the wire carries an integer. */
export function capOf(text: string): number {
  return Number(text.replaceAll(',', ''))
}

/**
 * `(cap − included) × unit_amount`, through `packages/shared` — see the header.
 *
 * For a cap the reader has **changed**. The stored cap's worst case is
 * `widget.worst_case`, which the API computed and answered with.
 *
 * Answers `null` for a cap below the allowance rather than throwing: `usageBillable`
 * refuses one with a `DomainError`, and a half-typed number must not take the page
 * down. `capsFormSchema` refuses it as a field error; this is the same rule stated
 * where a value is read rather than where a form is submitted.
 */
export function worstCaseAt(widget: WidgetView, cap: number): Money | null {
  if (!Number.isSafeInteger(cap) || cap < widget.included_qty) return null
  return worstCaseAtCap(widget.unit_amount, widget.included_qty, cap)
}
